/**
 * @ Author: Housefolios
 * @ Create Time: 2022-12-15 12:05:07
 * @ Modified by: David Helmick
 * @ Modified time: 2025-08-13 16:11:50
 * @ Description: Component to upload new images for proforma logos
 */

import React, { useEffect, useState } from 'react'

import { Button, Divider, Grid, Box } from '@mui/material'
import Alert from '@mui/material/Alert'
// Centralized lodash imports - much cleaner!
import { map, head } from '@/utils/lodash'

import { useDropzone } from 'react-dropzone'

import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone'
import CheckIcon from '@mui/icons-material/Check'
import { useMutation } from '@apollo/client/react'
import { IMAGE_UPLOAD } from '@/housefolios-components/TitleButtons/AddPropertyButton/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_ME } from '@/housefolios-components/Session/queries'

function ProformaLogoImagesUploaderComponent(props) {
  const [files, setFiles] = useState([])

  const [imageUpload] = useMutation(IMAGE_UPLOAD)

  const {
    isDragActive,
    isDragAccept,
    isDragReject,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: {
      'image/*': [],
    },
    onDrop: async (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      )
      await Promise.all(
        map(acceptedFiles, (file) => {
          const reader = new FileReader()
          return new Promise((resolve, reject) => {
            reader.onload = async () => {
              const image = reader.result
              await imageUpload({
                variables: {
                  documentId: props.session.me.activeOrg._id,
                  imageUrls: [image],
                  collection: 'proformaLogos',
                },
                refetchQueries: [{ query: GET_ME }],
              }).then(async ({ data  }) => {
                enqueueSnackbar('Image Saved', {
                  variant: 'success',
                  autoHideDuration: 3000,
                })
                resolve(image)
              })
            }
            reader.readAsDataURL(file)
          })
        }),
      )
    },
  })

  const thumbs = files.map((file) => (
    <Grid size={{ md: 4 }} key={file.name} sx={{ p: '0.5rem' }}>
      <Box style={{ width: '100%', borderColor: '#4f5062' }} sx={[{
        border: "1px solid rgba(79, 80, 98, 0.3)",
        boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)",
        overflow: "visible",
        "& .card-header": { display: "flex", alignItems: "center" },
      }, { p: '0.5rem', backgroundColor: '#fff', display: 'flex', overflow: 'hidden' }, { boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important" }]}>
        <Box component="img" src={file.preview} alt="..." sx={[{ maxWidth: '100%', height: 'auto', borderRadius: '0.29rem', }, {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
        }]} />
      </Box>
    </Grid>
  ))

  useEffect(
    () => () => {
      files.forEach((file) =>
        URL.revokeObjectURL(file ? file.preview : null),
      )
    },
    [files],
  )

  return (
    <>
      <Box sx={{ p: '1.5rem' }}>
        <Grid container spacing={6}>
          <Grid size={12}>
            <Box data-hf="dropzone" sx={{
              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
            }}>
              <Box
                {...getRootProps()} sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  outline: "none !important",
                  "& *": { outline: "none !important" },
                }}
              >
                <input {...getInputProps()} />
                <Box data-hf="dropzone-inner" sx={{
                  padding: "3rem 1rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  textAlign: "center",
                  color: "#a6a6b9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  border: "2px dashed #e6e7f1",
                  borderRadius: "0.65rem",
                  backgroundColor: "#f8f9ff",
                  "&:hover": { borderColor: "#a6a6b9", color: "#4f5062", background: "#f8f9ff" },
                  "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
                  "html[data-color-mode=\"dark\"] &:hover": { borderColor: "#368cff !important", color: "rgba(255, 255, 255, 0.87) !important", backgroundColor: "#333 !important" },
                  background: "transparent",
                  borderColor: "transparent",
                  borderWidth: "1px",
                }}>
                  {isDragAccept && (
                    <div>
                      <Box sx={[{
                        height: "100px !important",
                        width: "100px !important",
                        lineHeight: "100px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        boxShadow: "none",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                        mb: "1rem",
                        backgroundColor: "#1bc943",
                        borderRadius: "0.75rem",
                        color: "#fff",
                      }, { boxShadow: "0 0.46875rem 2.1875rem rgba(27, 201, 67, 0.03), 0 0.9375rem 1.40625rem rgba(27, 201, 67, 0.03), 0 0.25rem 0.53125rem rgba(27, 201, 67, 0.05), 0 0.125rem 0.1875rem rgba(27, 201, 67, 0.03) !important" }]}>
                        <CheckIcon  sx={{
                          height: "50px !important",
                          width: "50px !important",
                          lineHeight: "50px !important",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                        }}/>
                      </Box>
                      <Box sx={[{ color: '#1bc943' }, { fontSize: "0.8333333333rem !important" }]}>
                        Valid Images!
                      </Box>
                    </div>
                  )}
                  {isDragReject && (
                    <div>
                      <Box sx={[{
                        height: "100px !important",
                        width: "100px !important",
                        lineHeight: "100px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        boxShadow: "none",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                        mb: "1rem",
                        backgroundColor: "#f83245",
                        borderRadius: "0.75rem",
                        color: "#fff",
                      }, { boxShadow: "0 0.46875rem 2.1875rem rgba(248, 50, 69, 0.03), 0 0.9375rem 1.40625rem rgba(248, 50, 69, 0.03), 0 0.25rem 0.53125rem rgba(248, 50, 69, 0.05), 0 0.125rem 0.1875rem rgba(248, 50, 69, 0.03) !important" }]}>
                        <CloseTwoToneIcon  sx={{
                          height: "50px !important",
                          width: "50px !important",
                          lineHeight: "50px !important",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                        }}/>
                      </Box>
                      <Box sx={[{ color: '#f83245' }, { fontSize: "0.8333333333rem !important" }]}>
                        These files are not images!
                      </Box>
                    </div>
                  )}
                  {!isDragActive && (
                    <div>
                      <Box sx={[{
                        height: "90px !important",
                        width: "90px !important",
                        lineHeight: "90px !important",
                        display: "inline-flex !important",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        border: 0,
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        boxShadow: "none",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                        mb: "1rem",
                        backgroundColor: "#fff",
                        borderRadius: "0.75rem",
                      }, { boxShadow: "0 0.46875rem 2.1875rem rgba(244, 245, 253, 0.03), 0 0.9375rem 1.40625rem rgba(244, 245, 253, 0.03), 0 0.25rem 0.53125rem rgba(244, 245, 253, 0.05), 0 0.125rem 0.1875rem rgba(244, 245, 253, 0.03) !important" }]} style={{ color: '#4191ff' }}>
                        <CloudUploadTwoToneIcon  sx={{
                          height: "40px !important",
                          width: "40px !important",
                          lineHeight: "40px !important",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                        }}/>
                      </Box>
                      <Box sx={{ fontSize: "0.875rem !important" }}>
                        Drag and drop images here
                      </Box>
                    </div>
                  )}
                  <Box
                    component="small"
                    sx={{
                      py: '0.5rem',
                      color: 'rgba(7, 9, 25, 0.5)',
                    }}
                  >
                    or
                  </Box>
                  <div>
                    <Button
                     
                      sx={{
                        boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                        color: "#fff",
                        backgroundColor: "#368cff",
                        backgroundImage: "none",
                        borderColor: "#368cff",
                        "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                        "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                        "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                        "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                        "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { transform: "scale(1.08)", zIndex: 3 },
                        borderRadius: "4.5rem",
                        fontWeight: 700,
                        px: "1.5rem",
                      }}
                    >
                      <Box component="span" sx={{ px: '0.5rem' }}>
                        Browse Files
                      </Box>
                    </Button>
                  </div>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ p: '1rem', backgroundColor: '#f8f9ff' }}>
        {thumbs.length > 0 && (
          <div>
            <Alert
              severity="success"
              sx={{ textAlign: 'center', mb: '1rem' }}
            >
              You have uploaded <b>{thumbs.length}</b> files!
            </Alert>
            {/* <Grid container spacing={0}>
                 {thumbs}
               </Grid> */}
          </div>
        )}
      </Box>
      <Divider style={{ marginBottom: 5 }} />
    </>
  )
}

export default withSession(ProformaLogoImagesUploaderComponent)
